import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { toWords } from 'number-to-words';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { LoadingStatusIndicator } from '@dndbeyond/ddb-common-components/dist/Components/Listing';
import SubscriptionsBanner from '@dndbeyond/ddb-common-components/dist/Components/SubscriptionsBanner';
import useApiCall from '@dndbeyond/ddb-common-components/dist/Utils/useApiCall';

import config from '~/config';

import CharacterServiceApi from '../../api/CharacterServiceApi';
import useLocalStorage from '../../hooks/useLocalStorage';
import useSubscriptionTier, { FREE_TIER, HERO_TIER } from '../../hooks/useSubscriptionTier';
import useUserId from '../../hooks/useUserId';
import analytics from '../analytics';
import { CharacterData, CharacterStatusEnum, SortByPropMap, SortOrderEnum, SortTypeEnum } from '../typings';
import CharacterUtils from '../utils/CharacterUtils';
import SortUtils from '../utils/SortUtils';
import byProp from '../utils/byProp';
import CharacterCard from './CharacterCard';
import ConfirmationModal from './MUIConfirmationModal';

export interface UnlockConfirmationMessageProps {
    charactersToUnlock: Array<CharacterData>;
    maxCharacterSlotsAllowed: number | null;
    message?: React.ReactNode;
}
export const UnlockConfirmationMessage: React.FC<UnlockConfirmationMessageProps> = ({
    charactersToUnlock,
    maxCharacterSlotsAllowed,
    message = null,
}) => {
    // TODO: Character Slots update copy
    if (charactersToUnlock.length === 0) {
        return (
            <Typography>
                {`Are you sure you want to unlock no characters? All of your characters will be permanently locked until a new subscription is applied to your account, but you will be able to create and access up to ${maxCharacterSlotsAllowed} new characters. Locked characters do not count towards your character limit.`}
            </Typography>
        );
    }

    return (
        <Stack>
            <Box sx={{ marginBottom: 2 }}>
                {charactersToUnlock.map(CharacterUtils.getName).map((characterName) => (
                    <Chip label={characterName} sx={{ m: 1 }} />
                ))}
            </Box>
            {message && message}
        </Stack>
    );
};

interface FinalizeUnlockProps {
    charactersToUnlock: Array<CharacterData>;
    maxCharacterSlotsAllowed: number | null;
    finalizeUnlock: () => void;
    disabled: boolean;
}
const FinalizeUnlockButton: React.FC<FinalizeUnlockProps> = ({
    charactersToUnlock,
    finalizeUnlock,
    disabled,
    maxCharacterSlotsAllowed,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const charactersToUnlockCount = charactersToUnlock.length;

    return (
        <React.Fragment>
            <ConfirmationModal
                onClose={() => {
                    analytics.logUnlockFinishUnlockingCancelled(charactersToUnlockCount, maxCharacterSlotsAllowed);
                    setIsOpen(false);
                }}
                isOpen={isOpen}
                onConfirm={() => {
                    finalizeUnlock();
                    analytics.logUnlockFinishUnlockingConfirmed(charactersToUnlockCount, maxCharacterSlotsAllowed);
                    setIsOpen(false);
                }}
                message={
                    <UnlockConfirmationMessage
                        charactersToUnlock={charactersToUnlock}
                        maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                        message={
                            <Typography>
                                Other characters will remain deactivated until you free up a slot or add slots with a
                                D&D Beyond subscription.
                            </Typography>
                        }
                    />
                }
                title="Activate these characters?"
            />
            <Button
                size="large"
                variant="contained"
                disabled={disabled}
                onClick={() => {
                    setIsOpen(true);
                    analytics.logUnlockFinishUnlockingClicked(charactersToUnlockCount, maxCharacterSlotsAllowed);
                }}
            >
                Continue
            </Button>
        </React.Fragment>
    );
};

interface SecondaryHeaderProps
    extends Pick<MyCharactersListingProps, 'maxCharacterSlotsAllowed' | 'characters' | 'hasLockedCharacters'> {
    characterCount: number;
    subscriptionTier: string;
    characterIdsToUnlock: Array<number>;
}
const MyCharactersHeaderSecondary: React.FC<SecondaryHeaderProps> = ({
    hasLockedCharacters,
    maxCharacterSlotsAllowed,
    characterCount,
    subscriptionTier,
    characters,
    characterIdsToUnlock,
}) => {
    const [
        finalizeUnlock,
        isFinalizingUnlock,
        errorFinalizingUnlock,
        // Unlock state depends on context state passed by Waterdeep on the root element so we have to do a full page reload after a successful unlock.
    ] = useApiCall(CharacterServiceApi.unlockCharacters, () => window.location.reload());

    if (hasLockedCharacters) {
        return (
            <>
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
                    open={isFinalizingUnlock}
                    onClick={(evt) => {
                        evt.stopPropagation();
                        evt.nativeEvent.stopImmediatePropagation();
                    }}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Alert
                    severity="warning"
                    sx={{
                        marginBottom: '15px',
                        '& 	.MuiAlert-action': {
                            alignItems: 'center',
                        },
                    }}
                >
                    <AlertTitle>Character Slots Exceeded</AlertTitle>
                    {/* TODO: change FREE to Hero/Free/Master */}
                    Your current D&amp;D Beyond Membership is:
                    <strong>
                        {' '}
                        {subscriptionTier.toUpperCase()} ({maxCharacterSlotsAllowed} slots)
                    </strong>
                    . To continue, select up to{' '}
                    <strong>
                        {maxCharacterSlotsAllowed
                            ? `${toWords(maxCharacterSlotsAllowed)} (${maxCharacterSlotsAllowed}) `
                            : 'unlimited'}
                    </strong>
                    characters to activate. Your other characters will be deactivated until you restore them by deleting
                    an active character to free up a slot, or by adding more slots with a{` `}
                    <Link
                        href={`${config.ddbBaseUrl}/subscribe`}
                        onClick={() => analytics.logUnlockSubscribeClicked()}
                        sx={{
                            // color:'warning.dark' TODO???
                            color: 'rgb(102, 60, 0)',
                            textDecorationColor: 'rgb(102, 60, 0)',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                        }}
                    >
                        D&amp;D Beyond Subscription
                    </Link>
                </Alert>
                <Stack
                    component={Paper}
                    elevation={2}
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        zIndex: 2,
                        background: '#fff',
                        width: '100vw',
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="center"
                        spacing={2}
                        sx={{
                            padding: '8px',
                            maxWidth: '1200px !important',
                            width: '100%',
                            margin: '0 auto',
                        }}
                    >
                        <Box>
                            <Typography variant="h6" textAlign={'center'}>
                                <span style={{ fontWeight: '400' }}>Selected:</span> {characterIdsToUnlock.length} /{' '}
                                {maxCharacterSlotsAllowed}
                            </Typography>
                        </Box>

                        <FinalizeUnlockButton
                            charactersToUnlock={characters.filter((character) =>
                                characterIdsToUnlock.includes(CharacterUtils.getId(character)),
                            )}
                            finalizeUnlock={() => finalizeUnlock(characterIdsToUnlock)}
                            disabled={isFinalizingUnlock}
                            maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                        />
                    </Stack>
                </Stack>
            </>
        );
    }

    if (characterCount === 0) {
        return (
            <div className="ddb-characters-listing-count-active">
                Character slots allow players to create characters. Once created, all of your characters will appear in
                the list below.
            </div>
        );
    }

    return null;
};

const overrideStatusForUnlock = ({ character, characterIdsToUnlock, hasLockedCharacters }) => {
    if (!hasLockedCharacters) {
        return CharacterUtils.getStatus(character);
    }

    return characterIdsToUnlock.includes(CharacterUtils.getId(character))
        ? CharacterStatusEnum.Active
        : CharacterStatusEnum.Locked;
};

export interface MyCharactersListingProps {
    className?: string;
    isLoading: boolean;
    characters: Array<CharacterData>;
    loadingError?: Error | null;
    reload: () => void;
    hasLockedCharacters: boolean;
    maxCharacterSlotsAllowed: number | null;
    hasMaxCharacters: boolean;
}
export interface SortState {
    sortBy: SortTypeEnum;
    sortOrder: SortOrderEnum;
}
const MyCharactersListing: React.FC<MyCharactersListingProps> = ({
    className = '',
    isLoading,
    characters,
    loadingError,
    reload,
    hasLockedCharacters,
    maxCharacterSlotsAllowed,
    hasMaxCharacters,
}) => {
    const userId = useUserId();
    const subscriptionTier = useSubscriptionTier();

    const [sortPreference, setSortPreference] = useLocalStorage<string>(
        `MY_CHARACTERS_SORT_PREFERENCE:${userId}`,
        SortUtils.createSortValue(SortTypeEnum.Created, SortOrderEnum.Ascending),
    );

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<SortState>({
        sortBy: SortTypeEnum.Created,
        sortOrder: SortOrderEnum.Ascending,
    });

    useEffect(() => {
        // Don't try to set the sort if there is no preference or if the listing is currently loading results
        if (!sortPreference || isLoading) {
            return;
        }

        const [sortPreferenceBy, sortPreferenceOrder] = sortPreference.split('-');

        // If they are false then use the default
        if (!sortPreferenceBy || !sortPreferenceOrder) {
            return;
        }

        // Prevent an infinite loop of reloading once the sort preference has actually been applied
        if (sortPreferenceBy === sort.sortBy && sortPreferenceOrder === sort.sortOrder) {
            return;
        }

        setSort({
            sortBy: sortPreferenceBy as SortTypeEnum,
            sortOrder: sortPreferenceOrder as SortOrderEnum,
        });
    }, [isLoading, sortPreference, sort]);

    const [characterIdsToUnlock, setCharacterIdsToUnlock] = useState<Array<number>>([]);

    const canUnlockMore: boolean = maxCharacterSlotsAllowed
        ? characterIdsToUnlock.length < maxCharacterSlotsAllowed
        : true;

    useEffect(() => {
        if (hasLockedCharacters && characterIdsToUnlock.length === 0) {
            setCharacterIdsToUnlock(
                characters
                    .filter((character) => CharacterUtils.getStatus(character) === CharacterStatusEnum.Active)
                    .map(CharacterUtils.getId),
            );
        }
    }, [hasLockedCharacters, characters]);

    const unlockCharacter = useCallback(
        (character: CharacterData) => {
            if (!characterIdsToUnlock.includes(CharacterUtils.getId(character))) {
                setCharacterIdsToUnlock([...characterIdsToUnlock, CharacterUtils.getId(character)]);
            }
        },
        [characterIdsToUnlock],
    );

    const lockCharacter = useCallback(
        (character: CharacterData) => {
            if (characterIdsToUnlock.includes(CharacterUtils.getId(character))) {
                setCharacterIdsToUnlock(characterIdsToUnlock.filter((id) => id !== CharacterUtils.getId(character)));
            }
        },
        [characterIdsToUnlock],
    );

    let filteredCharacters = characters.filter((character) => {
        return CharacterUtils.getSearchableTerms(character).join(' ').toLowerCase().includes(search.toLowerCase());
    });

    const sortByPropSelector = SortByPropMap[sort.sortBy];

    if (sortByPropSelector) {
        filteredCharacters = filteredCharacters.sort(byProp(sortByPropSelector));

        if (sort.sortOrder === SortOrderEnum.Descending) {
            filteredCharacters.reverse();
        }
    }

    let lockedCharacters: Array<CharacterData> = [];
    if (!hasLockedCharacters && canUnlockMore) {
        lockedCharacters = filteredCharacters.filter(
            (character) => CharacterUtils.getStatus(character) === CharacterStatusEnum.Locked,
        );
        filteredCharacters = filteredCharacters.filter(
            (character) => CharacterUtils.getStatus(character) === CharacterStatusEnum.Active,
        );
    }

    const characterCount: number = filteredCharacters.length;
    const overCharacterLimit: boolean = maxCharacterSlotsAllowed !== null && characterCount >= maxCharacterSlotsAllowed;

    return (
        <div className={[className, 'ddb-characters-listing'].filter(Boolean).join(' ')}>
            <div className="ddb-characters-listing-header">
                <div className="ddb-characters-listing-header-primary">
                    {/*
                    The CTA component will be shown depending on some conditions:

                    - If the user doesn't have a subscription, and has 6 characters, show the Hero CTA
                    - If the user has a Hero subscription, show the Master CTA

                    Remember that the Images for the CTA's are served by Waterdeep (then, the images could be shown as broken)
                */}

                    {subscriptionTier === FREE_TIER && characterCount === maxCharacterSlotsAllowed && (
                        <div className="ddb-characters-listing-hero-cta">
                            <SubscriptionsBanner
                                iconType="hero"
                                text="Your party is full. Unlock unlimited character creation."
                                buttonLabel="Subscribe Now!"
                                onClick={() => (window.location.href = `${config.ddbBaseUrl}/store/subscribe#plans`)}
                            />
                        </div>
                    )}

                    {subscriptionTier.includes(HERO_TIER) && characterCount === maxCharacterSlotsAllowed && (
                        <div className="ddb-characters-listing-master-cta">
                            <SubscriptionsBanner
                                iconType="master"
                                text="Unlock your full potential and EXCLUSIVE perks."
                                buttonLabel="Upgrade Now!"
                                onClick={() => (window.location.href = `${config.ddbBaseUrl}/store/subscribe#plans`)}
                            />
                        </div>
                    )}

                    <div className="ddb-characters-listing-header-primary-title">
                        My Character Slots:{' '}
                        <span className="ddb-characters-listing-header-primary-title-count">
                            {characterCount}/
                            {/* TODO: Come back and use the subscription role to figure this out? This number won't be the same as our DB max int */}
                            {maxCharacterSlotsAllowed === null || maxCharacterSlotsAllowed === 2147483647
                                ? 'Unlimited'
                                : `${maxCharacterSlotsAllowed} Used`}
                        </span>
                    </div>
                    <div className="page-header__extra--content2">
                        <div className="more-links">
                            {!hasMaxCharacters ? (
                                <a href={`${config.basePathname}/builder`} className="button-alt button-alt-character">
                                    <span className="label">Create A Character</span>
                                </a>
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className="ddb-characters-listing-header-secondary">
                    <MyCharactersHeaderSecondary
                        hasLockedCharacters={hasLockedCharacters}
                        maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                        characterCount={characterCount}
                        characters={characters}
                        characterIdsToUnlock={characterIdsToUnlock}
                        subscriptionTier={subscriptionTier}
                    />
                </div>
            </div>
            <div className="ddb-characters-listing-actions">
                <div className="ddb-characters-listing-actions__search j-characters-listing__search">
                    <div className="ddb-characters-listing-actions__search-box" id="ddb-characters-listing-search">
                        <SearchIcon sx={{ width: '20px', marginRight: '5px', color: '#979AA4' }} />
                        <input
                            type="search"
                            className="j-characters-listing__search-input ddb-characters-listing-actions__search-input"
                            id="collapsible-search-input"
                            placeholder="Search by Name, Level, Class, Race, or Campaign"
                            value={search}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
                        />
                        <button
                            className={[
                                'ddb-characters-listing-actions__search-clear',
                                !search && 'ddb-characters-listing-actions__search-clear--hidden',
                                'j-characters-listing__search-clear',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={() => {
                                setSearch('');
                                analytics.logListingSearchCleared();
                            }}
                            aria-label="Clear Search"
                        >
                            Clear X
                        </button>
                    </div>
                </div>
                <div className="ddb-characters-listing-actions__sort">
                    <label className="ddb-characters-listing-actions__sort-label" htmlFor="ddb-characters-listing-sort">
                        Sort By
                    </label>
                    <select
                        className="ddb-characters-listing-actions__sort-box j-characters-listing__sort"
                        id="ddb-characters-listing-sort"
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            const { value } = event.target;

                            const [sortBy, sortOrder] = value.split('-');

                            setSort({
                                sortBy: sortBy as SortTypeEnum,
                                sortOrder: sortOrder as SortOrderEnum,
                            });

                            setSortPreference(value);

                            analytics.logListingSortChanged(value);
                        }}
                        value={SortUtils.createSortValue(sort.sortBy, sort.sortOrder)}
                    >
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Created, SortOrderEnum.Descending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Created: Newest
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Created, SortOrderEnum.Ascending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Created: Oldest
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Name, SortOrderEnum.Ascending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Name: A to Z
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Name, SortOrderEnum.Descending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Name: Z to A
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Level, SortOrderEnum.Ascending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Level: Low to High
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Level, SortOrderEnum.Descending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Level: High to Low
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Modified, SortOrderEnum.Descending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Modified: Latest
                        </option>
                        <option
                            value={SortUtils.createSortValue(SortTypeEnum.Modified, SortOrderEnum.Ascending)}
                            className="ddb-characters-listing-actions__sort-option"
                        >
                            Modified: Oldest
                        </option>
                    </select>
                </div>
            </div>
            <div className="ddb-characters-listing-body j-characters-listing__content">
                <div className="listing-container listing-container-ul RPGCharacter-listing">
                    <div className="listing-body">
                        <ul className="listing listing-rpgcharacter rpgcharacter-listing">
                            {filteredCharacters.map((character) => (
                                <CharacterCard
                                    key={`listing-item_${CharacterUtils.getId(character)}`}
                                    character={{
                                        ...character,
                                        status: overrideStatusForUnlock({
                                            character,
                                            characterIdsToUnlock,
                                            hasLockedCharacters,
                                        }),
                                    }}
                                    hasLockedCharacters={hasLockedCharacters}
                                    overCharacterLimit={overCharacterLimit}
                                    reloadListing={reload}
                                    lockCharacter={lockCharacter}
                                    unlockCharacter={unlockCharacter}
                                    canUnlockMore={canUnlockMore}
                                    maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                                    currentCharacterCount={characterCount}
                                />
                            ))}
                        </ul>
                    </div>
                    {lockedCharacters.length > 0 && (
                        <div>
                            <Typography component="h2" variant="h4" sx={{ fontWeight: 600 }}>
                                Deactivated Characters
                            </Typography>
                            <Divider sx={{ margin: '8px 0 16px' }} />
                            <Typography variant="body1">
                                Activate characters by freeing up a slot, or add slots with a
                                <Button
                                    variant="text"
                                    href={`${config.ddbBaseUrl}/subscribe`}
                                    sx={{
                                        '&:visited': {
                                            color: 'primary.main',
                                        },
                                        '&:hover': {
                                            color: 'primary.main',
                                        },
                                    }}
                                    onClick={() => analytics.logUnlockSubscribeClicked()}
                                >
                                    D&amp;D Beyond subscription.
                                </Button>
                            </Typography>
                            <div className="listing-body">
                                <ul className="listing listing-rpgcharacter rpgcharacter-listing">
                                    {lockedCharacters.map((character) => (
                                        <CharacterCard
                                            key={`listing-item_${CharacterUtils.getId(character)}`}
                                            character={{
                                                ...character,
                                                status: overrideStatusForUnlock({
                                                    character,
                                                    characterIdsToUnlock,
                                                    hasLockedCharacters,
                                                }),
                                            }} // TODO can this logic go in the utils?
                                            hasLockedCharacters={hasLockedCharacters}
                                            overCharacterLimit={overCharacterLimit}
                                            reloadListing={reload}
                                            lockCharacter={lockCharacter}
                                            unlockCharacter={unlockCharacter}
                                            canUnlockMore={canUnlockMore}
                                            maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                                            currentCharacterCount={characterCount}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="ddb-characters-listing__loading-indicator-wrapper">
                <LoadingStatusIndicator isLoading={isLoading} loadingError={loadingError} />
            </div>
            <div className="ddbcl-my-characters-listing__version">My Characters Version: v{config.version}</div>
        </div>
    );
};

export default MyCharactersListing;
